import { useState } from 'react';
import { HELP } from '@/constants/testIds';
import { BookOpen, ScrollText, Shield, GitBranch, Lightbulb, ChevronDown } from 'lucide-react';

const TABS = [
  { id: 'tutoriels', label: 'Tutoriels', icon: Lightbulb, testid: HELP.tabTutorials },
  { id: 'docs', label: 'Documentation', icon: BookOpen, testid: HELP.tabDocs },
  { id: 'rgpd', label: 'RGPD', icon: Shield, testid: HELP.tabRgpd },
  { id: 'version', label: 'Journal versions', icon: GitBranch, testid: HELP.tabVersion },
];

const TUTORIELS = [
  {
    q: "Comment créer un nouveau permis ?",
    a: "Depuis le menu latéral, cliquez sur « Nouveau permis ». Sélectionnez le type (Autorisation de Travail, Permis de Feu, ou Permis de Levage), puis renseignez les 3 étapes : Identification, Spécificités, Analyse HSE. Vous pouvez enregistrer en brouillon à tout moment.",
  },
  {
    q: "Qui peut approuver un permis ?",
    a: "Workflow standard : Demandeur soumet → Réviseur HSE révise → Approbateur Site approuve → Clôture. Les rôles sont attribués par un administrateur dans Utilisateurs.",
  },
  {
    q: "Comment signer électroniquement ?",
    a: "Sur la fiche du permis, descendez à la section « Signatures ». Sélectionnez votre rôle puis tracez votre signature au doigt (tablette) ou à la souris. Cliquez « Valider la signature ».",
  },
  {
    q: "Comment générer le PDF officiel ?",
    a: "Bouton « PDF » en haut de la fiche permis. Le PDF respecte le modèle Lafarge avec QR code de vérification.",
  },
  {
    q: "Que se passe-t-il hors ligne ?",
    a: "L'indicateur en haut à droite passe en orange. Vous pouvez continuer à consulter les permis déjà ouverts. La synchronisation reprend dès le retour de la connexion.",
  },
  {
    q: "Comment dupliquer un permis récurrent ?",
    a: "Bouton « Dupliquer » sur la fiche : crée un nouveau brouillon pré-rempli que vous pouvez ajuster.",
  },
];

function Accordion({ items, prefix }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="divide-y divide-stone-200 border border-stone-200">
      {items.map((it, i) => (
        <div key={i}>
          <button
            data-testid={`${prefix}-${i}`}
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-stone-50"
          >
            <span className="font-semibold text-sm text-[#09090B]">{it.q}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${open === i ? 'rotate-180' : ''}`} />
          </button>
          {open === i && (
            <div className="px-4 pb-4 text-sm text-stone-700 leading-relaxed border-t border-stone-100 bg-stone-50">
              <div className="pt-3">{it.a}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Help() {
  const [tab, setTab] = useState('tutoriels');

  return (
    <div className="max-w-5xl space-y-6">
      <div className="relative overflow-hidden border border-stone-200">
        <div
          className="h-40 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/8961008/pexels-photo-8961008.jpeg)' }}
        >
          <div className="h-full bg-gradient-to-r from-[#09090B] via-[#09090B]/80 to-transparent p-8 flex items-end">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-300">Module aide</div>
              <h1 className="text-3xl font-display font-black text-white mt-1">Centre d'aide & ressources</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-stone-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={t.testid}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 -mb-px ${
              tab === t.id ? 'border-[#DA291C] text-[#09090B]' : 'border-transparent text-stone-500 hover:text-[#09090B]'
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'tutoriels' && (
        <div className="space-y-4">
          <h2 className="text-xl font-display font-bold">Premiers pas — questions fréquentes</h2>
          <Accordion items={TUTORIELS} prefix="tuto" />
        </div>
      )}

      {tab === 'docs' && (
        <div className="section-card space-y-4 text-sm leading-relaxed text-stone-800">
          <h2 className="text-xl font-display font-bold">Documentation fonctionnelle</h2>
          <p><strong className="text-[#09090B]">Objectif :</strong> Plateforme de gestion des autorisations de travail pour les établissements Lafarge Ciments France. Remplace progressivement les Google Sheets utilisés en interne.</p>
          <h3 className="font-display font-bold mt-4">Workflow standard</h3>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Le <strong>Demandeur</strong> crée un permis (brouillon → soumis)</li>
            <li>Le <strong>Réviseur HSE</strong> contrôle le contenu (soumis → révisé ou rejeté)</li>
            <li>L'<strong>Approbateur Site</strong> valide l'autorisation (révisé → approuvé ou rejeté)</li>
            <li>Après exécution des travaux, l'Approbateur peut <strong>clôturer</strong> le permis</li>
          </ol>
          <h3 className="font-display font-bold mt-4">Documentation technique</h3>
          <p>Stack : React 19 (PWA) + FastAPI + MongoDB + Object Storage S3. Authentification Google SSO (Emergent Auth). PDF généré côté serveur avec ReportLab.</p>
          <h3 className="font-display font-bold mt-4">QR Code</h3>
          <p>Chaque PDF intègre un QR code de vérification au format <code className="bg-stone-100 px-1 font-mono text-xs">PERMIT:NUMERO</code> permettant l'identification rapide sur le terrain.</p>
        </div>
      )}

      {tab === 'rgpd' && (
        <div className="section-card space-y-4 text-sm leading-relaxed text-stone-800">
          <h2 className="text-xl font-display font-bold">Politique de confidentialité (RGPD)</h2>
          <p><strong>Responsable du traitement :</strong> Lafarge Ciments France SAS</p>
          <p><strong>Finalité du traitement :</strong> Gestion des autorisations de travail, traçabilité réglementaire HSE, communication interne sur les permis.</p>
          <p><strong>Données collectées :</strong> Nom, prénom, adresse email professionnelle, photo Google, sites attribués, rôle. Signatures électroniques (image). Métadonnées des permis (création, modification, signatures).</p>
          <p><strong>Base légale :</strong> Obligation légale (Code du Travail — sécurité des travailleurs) et intérêt légitime (sécurité industrielle).</p>
          <p><strong>Durée de conservation :</strong> Permis archivés 10 ans après clôture (obligation réglementaire). Comptes utilisateurs : durée d'emploi + 3 ans.</p>
          <p><strong>Droits :</strong> Conformément aux articles 15 à 22 du RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement, et d'opposition. Contactez votre DPO à <code className="bg-stone-100 px-1 font-mono text-xs">dpo@lafarge.fr</code>.</p>
          <p><strong>Hébergement :</strong> Serveurs en France (Union Européenne). Aucun transfert hors UE.</p>
          <p><strong>Sous-traitants :</strong> Google (authentification SSO), Emergent (hébergement plateforme et stockage).</p>
        </div>
      )}

      {tab === 'version' && (
        <div className="space-y-4">
          <h2 className="text-xl font-display font-bold">Journal des versions</h2>
          <div className="section-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-2xl font-display font-bold">v1.0.0 — MVP</div>
                <div className="text-xs text-stone-500 uppercase tracking-wider">Février 2026</div>
              </div>
              <span className="badge-status badge-approuve">Stable</span>
            </div>
            <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1">
              <li>Authentification Google SSO</li>
              <li>3 types de permis : Autorisation de Travail, Permis de Feu, Permis de Levage</li>
              <li>Workflow complet (brouillon → soumis → révisé → approuvé → clôturé)</li>
              <li>Signatures électroniques (canvas)</li>
              <li>Génération PDF Lafarge avec QR code</li>
              <li>20+ sites pré-configurés</li>
              <li>Gestion des rôles (Demandeur / Réviseur / Approbateur / Admin)</li>
              <li>Module Aide intégré + déclaration de bugs</li>
              <li>PWA — indicateur hors ligne</li>
            </ul>
          </div>
          <div className="section-card opacity-70">
            <div className="text-lg font-display font-bold">À venir — v1.1</div>
            <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mt-2">
              <li>Permis Travail en Hauteur, Nacelle, Espace Confiné, Fouille, Bordure d'Eau</li>
              <li>Attestation de Consignation Électrique, Bon LOTOTO</li>
              <li>Plans de Prévention & Entreprises Extérieures</li>
              <li>Notifications email (Resend)</li>
              <li>Synchronisation hors ligne (IndexedDB)</li>
              <li>Modèles PDF exactement répliqués à partir des Google Sheets fournis</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
